import { fromPromise } from 'xstate5';

import { getInitialWantedTime as getInitialWantedTimeUtil } from '../utils';
import { initialContext } from './ordering-machine.context';
import { orderingMachineLog } from './ordering-machine.logger';
import {
  type BagReward,
  type OrderingCart,
  type PartialLineItem,
  type PaymentMethod,
  type UpsellItem,
  type UpsellPlacement,
  type WantedTime,
} from './ordering-machine.types';

const fetchInitialData = fromPromise<FetchInitialDataResponse, FetchInitialDataParams>(async () => {
  orderingMachineLog.debug('Please provide `fetchInitialData` actor.');
  return {
    cart: initialContext.cart,
    paymentMethods: [],
    defaultPaymentMethodId: undefined,
    hasDefaultPaymentMethodCard: false,
    selectedTip: 0,
    availableTips: [],
    availablePoints: 0,
    availableCredit: 0,
    giftCardBalance: 0,
    customerId: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    stripeCustomerId: '',
    stripeCustomerKey: '',
    stripeSession: '',
    isNewCustomer: true,
    isOptedOutFromLoyalty: false,
    upsellItems: [],
  };
});

const fetchStatePostOrder = fromPromise<FetchStatePostOrderResponse, FetchStatePostOrderParams>(
  async () => {
    orderingMachineLog.debug('Please provide `fetchStatePostOrder` actor.');
    return {
      order: { id: '', hasEarnedLoyaltyPoints: false },
      cart: initialContext.cart,
    };
  },
);

const getInitialWantedTime = fromPromise<GetInitialWantedTimeResponse, GetInitialWantedTimeParams>(
  ({ input }) => {
    return getInitialWantedTimeUtil(input.orderId, input.wantedTimes);
  },
);

const fetchRewards = fromPromise<FetchRewardsResponse, FetchRewardsParams>(async () => {
  orderingMachineLog.debug('Please provide `fetchRewards` actor.');
  return { rewards: [] };
});

const fetchWantedTimes = fromPromise<FetchWantedTimesResponse, FetchWantedTimesParams>(async () => {
  orderingMachineLog.debug('Please provide `fetchWantedTimes` actor.');
  return { wantedTimes: [] };
});

const fetchBagCartPolling = fromPromise<FetchBagCartPollingResponse, FetchBagCartPollingParams>(
  async () => {
    orderingMachineLog.debug('Please provide `fetchBagCartPolling` actor.');
    return { cart: initialContext.cart };
  },
);

const addUpsellToBag = fromPromise<AddUpsellToBagResponse, AddUpsellToBagParams>(async () => {
  orderingMachineLog.debug('Please provide `addUpsellToBag` actor.');
  return {
    cart: initialContext.cart,
  };
});

const updateLineItem = fromPromise<UpdateLineItemResponse, UpdateLineItemParams>(async () => {
  orderingMachineLog.debug('Please provide `updateLineItem` actor.');
  return initialContext.cart;
});

const applyReward = fromPromise<ApplyRewardResponse, ApplyRewardParams>(async () => {
  orderingMachineLog.debug('Please provide `applyReward` actor.');
  return {
    error: 'Not implemented',
    isRewardMissingSku: false,
  };
});

const removeReward = fromPromise<RemoveRewardResponse, RemoveRewardParams>(async () => {
  orderingMachineLog.debug('Please provide `removeReward` actor.');
  return {
    error: 'Not implemented',
    isRewardMissingSku: false,
  };
});

const applyPromoCode = fromPromise<ApplyPromoCodeResponse, ApplyPromoCodeParams>(async () => {
  orderingMachineLog.debug('Please provide `applyPromoCode` actor.');
  return { rewards: [], success: false, error: undefined, isRewardMissingSku: false }; // prettier-ignore
});

const applyGiftCard = fromPromise<ApplyGiftCardResponse, ApplyGiftCardParams>(async () => {
  orderingMachineLog.debug('Please provide `applyGiftCard` actor.');
  return null;
});

const submitOrder = fromPromise<SubmitOrderResponse, SubmitOrderParams>(async () => {
  orderingMachineLog.debug('Please provide `submitOrder` actor.');
  return { success: false, isTimeslotUnavailable: false, error: undefined };
});

const leaveGroupOrder = fromPromise<LeaveGroupOrderResponse, LeaveGroupOrderParams>(async () => {
  orderingMachineLog.debug('Please provide `leaveGroupOrder` actor.');
  return { success: false };
});

export const OrderingActors = {
  fetchInitialData,
  fetchStatePostOrder,
  getInitialWantedTime,
  fetchRewards,
  fetchWantedTimes,
  fetchBagCartPolling,
  addUpsellToBag,
  updateLineItem,
  applyReward,
  removeReward,
  applyPromoCode,
  applyGiftCard,
  submitOrder,
  leaveGroupOrder,
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type FetchInitialDataParams = void;

export type FetchInitialDataResponse = {
  cart: Readonly<OrderingCart>;
  paymentMethods: readonly PaymentMethod[];
  defaultPaymentMethodId: string | undefined;
  hasDefaultPaymentMethodCard: boolean;
  selectedTip: number;
  availableTips: ReadonlyArray<number | 'CUSTOM'>;
  availablePoints: number;
  availableCredit: number;
  giftCardBalance: number;
  customerId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  stripeCustomerId: string;
  stripeCustomerKey: string;
  stripeSession: string;
  isNewCustomer: boolean;
  isOptedOutFromLoyalty: boolean;
  upsellItems: UpsellItem[];
};

export type FetchStatePostOrderParams = {
  orderId?: string;
};

export type FetchStatePostOrderResponse = {
  order: { id: string; hasEarnedLoyaltyPoints: boolean };
  cart: Readonly<OrderingCart>;
};

export type GetInitialWantedTimeParams = {
  orderId: string;
  wantedTimes: WantedTime[];
};

export type GetInitialWantedTimeResponse = WantedTime;

export type FetchRewardsParams = void;

export type FetchRewardsResponse = {
  rewards: readonly BagReward[];
};

export type FetchWantedTimesParams = void;

export type FetchWantedTimesResponse = {
  wantedTimes: WantedTime[];
};

export type FetchBagCartPollingParams = void;

export type FetchBagCartPollingResponse = {
  cart: Readonly<OrderingCart>;
};

export type AddUpsellToBagParams = {
  productId: string;
  productName: string;
  quantity: number;
  placementVariation?: UpsellPlacement;
  cart: OrderingCart;
  deliveryDetails?: {
    addressId: string;
    deliveryFee: number;
    tip: number;
    vendor: string;
    vendorRestaurantId: string;
  };
};

export type AddUpsellToBagResponse =
  | {
      cart: OrderingCart;
    }
  | {
      error: string;
    };

export type UpdateLineItemParams = {
  lineItemId: string;
  productId: string;
  quantity: number;
  resetQuantity?: () => void;
  lineItems: readonly PartialLineItem[];
};

export type UpdateLineItemResponse = OrderingCart | undefined;

export type ApplyRewardParams = {
  orderId: string;
  rewardId: string;
};

export type ApplyRewardResponse =
  | {
      cart: Readonly<OrderingCart>;
    }
  | {
      error: string;
      isRewardMissingSku: boolean;
    };

export type RemoveRewardParams = {
  orderId: string;
};

export type RemoveRewardResponse =
  | {
      cart: Readonly<OrderingCart>;
    }
  | {
      error: string;
    };

export type ApplyPromoCodeParams = {
  code: string;
};

export type ApplyPromoCodeResponse = {
  rewards: readonly BagReward[];
  success: boolean;
  error: string | undefined;
  isRewardMissingSku: boolean;
};

export type ApplyGiftCardParams = {
  code: string;
  regCode: string;
  awsWafToken: string;
};

export type ApplyGiftCardResponse = {
  giftCardBalance: number;
  availableCredit: number;
} | null;

export type SubmitOrderParams = {
  cart: Readonly<OrderingCart>;
  paymentMethodOrIntentId?: string;
  dropoffLocationId?: string;
  wantedTime?: WantedTime;
  selectedTip: number;
  shouldUseCredit: boolean;
  shouldIncludeUtensils: boolean;
};

export type SubmitOrderResponse = {
  success: boolean;
  isTimeslotUnavailable: boolean;
  error: string | undefined;
  order?: {
    id?: string;
    hasEarnedLoyaltyPoints?: boolean;
  };
};

export type LeaveGroupOrderParams = void;

export type LeaveGroupOrderResponse = {
  success: boolean;
};
